/**
 * SOLID Review
 * S — Good. One concern: the table's video chat — its policy as the Domain decides it, and the server's
 *     signaling and admin reads around the SDK the Framework drives. No rule lives here.
 * O — Good. A new media act is one more shell of the same shape.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Exactly the media ops of `ClientUseCases`.
 * D — Strong. Two port interfaces and the shared steps, all injected.
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type {
  ICommandMediaClock, ICommandMediaIdle, ICommandMediaBanner, ICommandMediaUsed, IPayloadMediaToken, IEntityMediaToken,
  IPayloadMediaConnection, IPayloadMediaHealth, IEntityMediaHealth, IPayloadMediaUsage, IEntityMediaUsage,
} from '../../domain/ports/schemas/MediaMoves';
import type { IPortClientMediaServer } from '../ports/contracts';
import { DONE, type ClientSteps, type TOutcome, type TResult } from '../shared/ClientSteps';

export interface IConfigDepMediaContext {
  readonly decisions: IPortClientDecisions;
  readonly media: IPortClientMediaServer;
  /** The one serial section, shared with every other context. */
  readonly steps: ClientSteps;
}

/**
 * THE TABLE'S VIDEO CHAT (frontend strict-ports plan, FE5b/FE5c, 2026-10-04). useLiveKitSession called the
 * Framework's LiveKit adapter for the token, the connection reports and the presence marks, and kept the
 * opt-in, the idle rule and the banner in React state; ControlPanel and the usage report called the same
 * adapter for their admin reads. The SDK's room, tracks and participants stay the hook's. What is here:
 *
 *   - the POLICY, each a session decision: on/off, reconnect, the idle tick (§23.7), the server's removal
 *     (§21.10), the banner, and presence — reported once per kind per room, never for a guest;
 *   - the SIGNALING, passed through with the room and player the SDK connected — which may be a room the
 *     table has just left, so these cannot read the session;
 *   - the ADMIN reads, passed through.
 */
export class MediaContext {
  constructor(private readonly deps: IConfigDepMediaContext) {}

  async toggleMedia(ctx: IContextRequest, command: ICommandMediaClock): Promise<TOutcome> {
    return this.step(ctx, (input) => this.deps.decisions.mediaToggled(ctx, input, command));
  }

  async reconnectMedia(ctx: IContextRequest, command: ICommandMediaClock): Promise<TOutcome> {
    return this.step(ctx, (input) => this.deps.decisions.mediaReconnect(ctx, input, command));
  }

  /** One tick of the idle check — `ClientJobs` runs it while video is on at a table. */
  async checkMediaIdle(ctx: IContextRequest, command: ICommandMediaIdle): Promise<TOutcome> {
    return this.step(ctx, (input) => this.deps.decisions.mediaIdleChecked(ctx, input, command));
  }

  async mediaRemovedByServer(ctx: IContextRequest, command: ICommandNothing): Promise<TOutcome> {
    return this.step(ctx, (input) => this.deps.decisions.mediaRemovedByServer(ctx, input, command));
  }

  async dismissMediaBanner(ctx: IContextRequest, command: ICommandMediaBanner): Promise<TOutcome> {
    return this.step(ctx, (input) => this.deps.decisions.mediaBannerDismissed(ctx, input, command));
  }

  /** The player's microphone or camera came on: reported once per kind per room — best-effort. */
  async mediaUsed(ctx: IContextRequest, command: ICommandMediaUsed): Promise<TOutcome> {
    const { decisions, steps, media } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planPresence(ctx, input, command));
    if (!plan.success) return plan;
    if (plan.value.request === null) return DONE;
    return media.markPresence(ctx, plan.value.request);
  }

  async mediaToken(ctx: IContextRequest, command: IPayloadMediaToken): Promise<TResult<IEntityMediaToken>> {
    return this.deps.media.token(ctx, command);
  }

  async reportMediaConnection(ctx: IContextRequest, command: IPayloadMediaConnection): Promise<TOutcome> {
    return this.deps.media.reportConnection(ctx, command);
  }

  async checkMediaHealth(ctx: IContextRequest, command: IPayloadMediaHealth): Promise<TResult<IEntityMediaHealth>> {
    return this.deps.media.health(ctx, command);
  }

  async loadMediaUsageReport(ctx: IContextRequest, command: IPayloadMediaUsage): Promise<TResult<IEntityMediaUsage>> {
    return this.deps.media.usageReport(ctx, command);
  }

  private async step(ctx: IContextRequest, decide: Parameters<ClientSteps['session']>[1]): Promise<TOutcome> {
    const done = await this.deps.steps.session(ctx, decide);
    return done.success ? DONE : done;
  }
}
